import { styled } from '@uhg-abyss/web/tools/styled';
import merge from 'lodash/merge';
import MarkdownToJSX from 'markdown-to-jsx';
import { ComponentProps } from 'react';

const MarkdownContainer = styled('div', {});

type MarkdownOptions = ComponentProps<typeof MarkdownToJSX>['options'];

const defaultOptions: MarkdownOptions = {
  overrides: {
    a: {
      props: {
        target: '_blank',
      },
    },
    ul: {
      props: {
        style: { paddingLeft: '1rem', listStyleType: 'disc' },
      },
    },
    ol: {
      props: {
        style: { paddingLeft: '1rem', listStyleType: 'decimal' },
      },
    },
  },
};

type MarkdownProps = {
  /** Markdown string to render. */
  children?: string;
  /**
   * markdown-to-jsx options. Use `overrides` to swap a rendered HTML tag for an
   * Abyss component, e.g. `{ overrides: { a: { component: Link } } }`.
   */
  options?: MarkdownOptions;
} & /**
 * Any remaining props are forwarded to the rendered markdown root,
 * e.g. `className`, `id`, `data-testid`, `data-auto-testid` and other
 * `data-*`, `aria-*` attributes.
 *
 * To apply custom styles, create a styled wrapper, e.g.
 * `const StyledMarkdown = styled(Markdown, { ... });`. On that wrapper you can
 * also pass a `css` prop to override styles (abyss `styled` consumes `css` and
 * turns it into a class).
 *
 * Do NOT pass a `css` prop directly to `Markdown` itself — it would leak onto
 * the rendered DOM node as `css="[object Object]"`.
 */ Omit<ComponentProps<typeof MarkdownContainer>, 'children'>;

export function Markdown({
  options,
  children = '',
  ...containerProps
}: MarkdownProps) {
  const markdownOptions = options
    ? merge({}, defaultOptions, options)
    : defaultOptions;

  return (
    <MarkdownContainer>
      {children && (
        <MarkdownToJSX {...containerProps} options={markdownOptions}>
          {children}
        </MarkdownToJSX>
      )}
    </MarkdownContainer>
  );
}
